import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import posed from 'react-pose';
import noop from 'lodash/noop';

import Collapsible from 'common/collapsible';
import Link from 'common/link';
import Icon from 'common/icon';

const sections = [
    {
        title: 'For Nonprofits',
        links: [
            { url: '/nonprofits/nonprofit-solutions', label: 'Nonprofit Solutions' },
            { url: '/nonprofits/peer-to-peer-fundraising', label: 'Peer-to-Peer' },
            { url: '/nonprofits/events-ticketing', label: 'Events & Ticketing' },
            { url: '/nonprofits/golf-marathons', label: 'Golf Marathon' },
            { url: '/nonprofits/run-walk-ride', label: 'Run, Walk, Ride' },
            {
                url: '/nonprofits/virtual-hybrid-challenges',
                label: 'Virtual and Hybrid Challenges',
            },
            { url: '/nonprofits/integrations', label: 'Integrations' },
            { url: '/pricing', label: 'Pricing' },
        ],
    },
    {
        title: 'For Individuals',
        links: [
            { url: '/individuals/how-it-works', label: 'How it Works' },
            { url: '/individuals/product-tour', label: 'Product Tour' },
            { url: '/individuals/current-campaigns/individuals', label: 'Current Campaigns' },
            { url: '/individuals/faqs', label: 'FAQs' },
            { url: '/pricing', label: 'Pricing' },
        ],
    },
    {
        title: 'Resources',
        links: [
            { url: '/blog', label: 'Blog' },
            { url: '/blog/category/press', label: 'Press' },
            { url: '/blog/category/tips-and-guides', label: 'Best Practices' },
            { url: '/blog/category/case-studies', label: 'Case Studies' },
            { url: '/blog/category/champions', label: 'Champions' },
        ],
    },
    {
        title: 'Support',
        links: [{ url: 'https://support.pledgeit.org/hc/en-us/requests/new', label: 'Contact Us' }],
    },
];

const SectionPose = posed.div({
    expanded: {
        height: 'auto',
        delayChildren: 100,
        staggerChildren: 25,
    },
    collapsed: {
        height: 0,
        staggerChildren: 25,
    },
});

const LinkPose = posed.div({
    expanded: { translateY: 0, opacity: 1 },
    collapsed: { translateY: -10, opacity: 0 },
});

class Sitemap extends Component {
    static propTypes = {
        showHome: PropTypes.bool,
        showSearch: PropTypes.bool,
        showSignIn: PropTypes.bool,
        collapsible: PropTypes.bool,
        className: PropTypes.string,
        sectionClassName: PropTypes.string,
        titleClassName: PropTypes.string,
        linkClassName: PropTypes.string,
        onSearch: PropTypes.func,
    };

    static defaultProps = {
        showHome: false,
        showSearch: false,
        showSignIn: false,
        collapsible: false,
        className: '',
        sectionClassName: '',
        titleClassName: '',
        linkClassName: '',
        onSearch: noop,
    };

    get classes() {
        const { sectionClassName, titleClassName, linkClassName, collapsible } = this.props;

        return {
            section: cx('relative pr-8 text-xs font-medium', sectionClassName),
            title: cx(
                'text-sm uppercase font-bold tracking-wide leading-tight text-white',
                titleClassName,
                { 'cursor-auto': !collapsible }
            ),
            link: cx('block my-4 text-gray-base hover:text-white', linkClassName),
        };
    }

    renderStandalone({ url, label, legacy }) {
        return (
            <section className={this.classes.section}>
                <Link to={url} className={this.classes.title} legacy={legacy}>
                    {label}
                </Link>
            </section>
        );
    }

    renderSection({ title, links }) {
        const { collapsible } = this.props;

        return (
            <Collapsible key={title} locked={!collapsible} startCollapsed={collapsible}>
                {({ isExpanded, toggle, collapse }) => (
                    <section className={this.classes.section}>
                        <h4>
                            <button type="button" className={this.classes.title} onClick={toggle}>
                                {title}
                            </button>
                        </h4>
                        <SectionPose
                            className="overflow-hidden"
                            pose={isExpanded ? 'expanded' : 'collapsed'}
                        >
                            {links.map(link => (
                                <LinkPose key={link.label}>
                                    <Link
                                        className={this.classes.link}
                                        to={link.url}
                                        onClick={collapse}
                                        legacy
                                    >
                                        {link.label}
                                    </Link>
                                </LinkPose>
                            ))}
                        </SectionPose>
                    </section>
                )}
            </Collapsible>
        );
    }

    render() {
        const { showHome, showSignIn, showSearch, className, onSearch } = this.props;

        return (
            <nav className={className}>
                {showHome && this.renderStandalone({ url: '/', label: 'Home', legacy: true })}

                {sections.slice(0, 2).map(section => this.renderSection(section))}
                {this.renderStandalone({ url: '/pricing', label: 'Pricing', legacy: true })}
                {sections.slice(2).map(section => this.renderSection(section))}

                {showSignIn &&
                    this.renderStandalone({ url: '/sign-in', label: 'Sign In', legacy: false })}

                {this.renderStandalone({
                    url: '/choice',
                    label: 'Get Started',
                    legacy: true,
                    titleClassName: 'text-green',
                })}
                {showSearch && (
                    <section className={this.classes.section}>
                        <button type="button" className={this.classes.title} onClick={onSearch}>
                            <Icon icon="search" type="solid" className="mr-1" /> Search
                        </button>
                    </section>
                )}
            </nav>
        );
    }
}

export default Sitemap;
